import { FunctionComponent, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { CreateKidDocumentValues } from '../CreateKidDocumentModal';

const StepTemplates: FunctionComponent = () => {
  const { register, watch, setValue } =
    useFormContext<CreateKidDocumentValues>();
  const templates = watch('templates');
  const creationMethod = watch('creationMethod');
  const [detailsKey, setDetailsKey] = useState<string | null>(null);

  // Auto-select templates based on creation method
  useEffect(() => {
    if (creationMethod === 'blank') {
      setValue(
        'templates',
        {
          globalRiskDisclosure: false,
          companyInformationBlock: false,
          performanceDisclaimer: false,
          investmentObjectiveStatement: false,
        },
        { shouldDirty: true, shouldValidate: false },
      );
    } else {
      setValue(
        'templates',
        {
          globalRiskDisclosure: true,
          companyInformationBlock: true,
          performanceDisclaimer: true,
          investmentObjectiveStatement: true,
        },
        { shouldDirty: true, shouldValidate: false },
      );
    }
    // Only react to method changes
  }, [creationMethod, setValue]);

  const TemplateRow: FunctionComponent<{
    name: keyof CreateKidDocumentValues['templates'];
    label: string;
    description: string;
    templateType: 'Manco' | 'Subfund';
  }> = ({ name, label, description }) => (
    <div className="flex items-center justify-between rounded-md border p-3 hover:bg-gray-50">
      <label className="flex flex-1 cursor-pointer items-start gap-3">
        <input type="checkbox" {...register(`templates.${name}` as any)} />
        <div>
          <div className="text-sm font-semibold text-gray-800">{label}</div>
          <div className="text-xs text-gray-600">{description}</div>
        </div>
      </label>
      <button
        type="button"
        className="ml-3 rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-100"
        onClick={() => setDetailsKey(name)}
      >
        View Details
      </button>
    </div>
  );

  const selectedCount = Object.entries(templates).filter(([, v]) => v).length;

  return (
    <div className="space-y-6">
      <div>
        <h4 className="mb-1 text-sm font-semibold text-gray-800">
          Manco Templates
        </h4>
        <p className="mb-3 text-xs text-gray-500">
          Global templates for all share classes
        </p>
        <div className="space-y-2">
          <TemplateRow
            name="globalRiskDisclosure"
            label="Global Risk Disclosure"
            description="Standard risk warnings and disclaimers applicable to all investment products..."
            templateType="Manco"
          />
          <TemplateRow
            name="companyInformationBlock"
            label="Company Information Block"
            description="Management company details, registration information, and contact details..."
            templateType="Manco"
          />
          <TemplateRow
            name="performanceDisclaimer"
            label="Performance Disclaimer"
            description="Past performance disclaimers and forward-looking statement warnings..."
            templateType="Manco"
          />
        </div>
      </div>

      <div>
        <h4 className="mb-1 text-sm font-semibold text-gray-800">
          Subfund Templates
        </h4>
        <p className="mb-3 text-xs text-gray-500">Specific to one fund</p>
        <TemplateRow
          name="investmentObjectiveStatement"
          label="Investment Objective Statement"
          description="Fund-specific investment objectives, strategy, and target outcomes..."
          templateType="Subfund"
        />
      </div>

      <div className="rounded-md bg-gray-50 p-3 text-xs text-gray-600">
        Selected: {selectedCount} templates
      </div>

      {detailsKey && (
        <div className="rounded-md border border-gray-200 p-4">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-800">
              Template Details
            </h4>
            <button
              type="button"
              className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-100"
              onClick={() => setDetailsKey(null)}
            >
              Close
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h5 className="mb-1 text-xs font-semibold text-gray-700">
                Template Information
              </h5>
              <div className="mb-2 text-xs text-gray-700">
                Template Type:{' '}
                <span className="font-medium">
                  {detailsKey === 'investmentObjectiveStatement'
                    ? 'Subfund'
                    : 'Manco'}
                </span>
              </div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">
                Template Text
              </label>
              <textarea
                className="h-28 w-full rounded border border-gray-300 p-2 text-xs"
                placeholder="Example content for this template..."
                readOnly
                value={
                  detailsKey === 'globalRiskDisclosure'
                    ? 'Standard risk warnings and disclaimers...'
                    : detailsKey === 'companyInformationBlock'
                      ? 'Management company details and contact information...'
                      : detailsKey === 'performanceDisclaimer'
                        ? 'Past performance disclaimers and forward-looking statements...'
                        : 'Investment objectives, strategy, and target outcomes...'
                }
              />
            </div>
            <div>
              <h5 className="mb-1 text-xs font-semibold text-gray-700">
                Template Associations
              </h5>
              <ul className="space-y-1 text-xs text-gray-700">
                <li>European Growth Fund — EUR ACC (EU001234567)</li>
                <li>European Value Fund — EUR DIS (EU001234568)</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepTemplates;


